import {
  buildXgenPageDataFromNuxtPayload,
  type XgenPageData,
} from '@/utils/xgenPageLoad'

declare global {
  interface Window {
    pageData: XgenPageData | undefined
  }
}

/** Fires a custom event to notify Xgen integration of data change */
const fireXgenPageLoadEvent = (pageData: XgenPageData) => {
  if (window) {
    window.dispatchEvent(
      new CustomEvent('xgen-page-load', {
        detail: pageData,
      })
    )
  }
}

/**
 * Plugin to handle XGen page data hooks lifecycle.
 *
 * Server-side:
 * - Hooks into app:rendered to build and save XGen page data to Nuxt payload
 *
 * Client-side:
 * - Hooks into page:finish to handle XGen page data updates after navigation
 *
 * Route change and query param handling is now managed in the xgenPageData global middleware.
 * @see ./packages/global/middleware/xgenPageData.global.ts
 */
export default defineNuxtPlugin(nuxtApp => {
  // Server-side logic: Hook into app:rendered to build and save it to payload for client side use
  if (import.meta.server) {
    nuxtApp.hook('app:rendered', () => {
      const pageData: XgenPageData = buildXgenPageDataFromNuxtPayload()

      const payload = nuxtApp.payload
      payload.pageData = pageData
    })

    return
  }

  // Client-side logic: Hook into page:finish to update pageData for navigation
  nuxtApp.hook('page:finish', () => {
    console.log('[XGEN: client] page finished loading, processing page data...')
    nextTick(() => {
      const payload = useNuxtApp().payload

      // ON FIRST LOAD: use the pageData mapped server-side and saved on the payload
      if (payload.pageData) {
        Object.assign(window, { pageData: payload.pageData })
        fireXgenPageLoadEvent(payload.pageData as XgenPageData)

        payload.pageData = undefined // Clear to avoid reuse
        return
      } else {
        const mappedFallbackData = buildXgenPageDataFromNuxtPayload()

        Object.assign(window, { pageData: mappedFallbackData })
        fireXgenPageLoadEvent(mappedFallbackData)
      }
    })
  })
})
